"use client";

import { type ReactNode, useMemo, useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";

/* ------------------------------------------------------------------ */
/*  Cliché list (module level)                                        */
/* ------------------------------------------------------------------ */

const CLICHES: string[] = [
  "at the end of the day",
  "think outside the box",
  "in this day and age",
  "the fact of the matter",
  "when all is said and done",
  "at this moment in time",
  "avoid like the plague",
  "back to square one",
  "ballpark figure",
  "beat around the bush",
  "benefit of the doubt",
  "best of both worlds",
  "better late than never",
  "bite the bullet",
  "blessing in disguise",
  "break the ice",
  "bring to the table",
  "burning the midnight oil",
  "calm before the storm",
  "can of worms",
  "cutting edge",
  "dead as a doornail",
  "diamond in the rough",
  "easier said than done",
  "every cloud has a silver lining",
  "few and far between",
  "fit as a fiddle",
  "for all intents and purposes",
  "go the extra mile",
  "the grass is always greener",
  "hit the ground running",
  "hit the nail on the head",
  "in the nick of time",
  "it goes without saying",
  "it is what it is",
  "last but not least",
  "leave no stone unturned",
  "level playing field",
  "light at the end of the tunnel",
  "like a kid in a candy store",
  "low-hanging fruit",
  "missed the boat",
  "moving forward",
  "needle in a haystack",
  "needless to say",
  "no-brainer",
  "off the beaten path",
  "old as time",
  "on the same page",
  "once in a blue moon",
  "only time will tell",
  "out of the blue",
  "par for the course",
  "part and parcel",
  "piece of cake",
  "plenty of fish in the sea",
  "pushing the envelope",
  "put all your eggs in one basket",
  "raining cats and dogs",
  "read between the lines",
  "scared to death",
  "sick as a dog",
  "sleep like a baby",
  "slippery slope",
  "speak of the devil",
  "take it to the next level",
  "the ball is in your court",
  "the best thing since sliced bread",
  "the bottom line",
  "the tip of the iceberg",
  "the whole nine yards",
  "tried and true",
  "up in the air",
  "win-win situation",
  "with all due respect",
  "word of mouth",
  "the writing on the wall",
  "actions speak louder than words",
  "add insult to injury",
  "against all odds",
  "ahead of the curve",
  "all walks of life",
  "an arm and a leg",
  "armed to the teeth",
  "as luck would have it",
  "at the drop of a hat",
];

/* Fresher alternatives for ~20 of the most common clichés */
const ALTERNATIVES: Record<string, string> = {
  "at the end of the day": "ultimately",
  "think outside the box": "approach it creatively",
  "in this day and age": "today",
  "the fact of the matter": "in fact",
  "when all is said and done": "ultimately",
  "at this moment in time": "now",
  "few and far between": "rare",
  "for all intents and purposes": "effectively",
  "hit the ground running": "start quickly",
  "it goes without saying": "clearly",
  "last but not least": "finally",
  "leave no stone unturned": "search thoroughly",
  "low-hanging fruit": "the easiest wins",
  "moving forward": "from now on",
  "needless to say": "naturally",
  "on the same page": "in agreement",
  "the bottom line": "the key point",
  "easier said than done": "difficult in practice",
  "avoid like the plague": "avoid entirely",
  "win-win situation": "a mutually beneficial outcome",
};

const RE_CLICHE = new RegExp(`\\b(?:${CLICHES.join("|")})\\b`, "gi");
const RE_SPLIT_WORDS = /\s+/;

/* ------------------------------------------------------------------ */
/*  Analysis                                                          */
/* ------------------------------------------------------------------ */

interface ClicheFinding {
  phrase: string;
  count: number;
  alternative?: string;
}

interface AnalysisResult {
  wordCount: number;
  totalMatches: number;
  findings: ClicheFinding[];
  highlighted: ReactNode[];
}

function buildHighlighted(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(RE_CLICHE)) {
    if (match.index > last) {
      nodes.push(text.slice(last, match.index));
    }
    nodes.push(
      <mark
        className="rounded bg-yellow-200 px-0.5 dark:bg-yellow-900/60 dark:text-yellow-100"
        key={`cliche-${match.index}`}
      >
        {match[0]}
      </mark>
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    nodes.push(text.slice(last));
  }
  return nodes;
}

function analyzeCliches(text: string): AnalysisResult | null {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return null;
  }

  const counts = new Map<string, number>();
  let totalMatches = 0;
  for (const match of text.matchAll(RE_CLICHE)) {
    const key = match[0].toLowerCase();
    counts.set(key, (counts.get(key) ?? 0) + 1);
    totalMatches += 1;
  }

  const findings: ClicheFinding[] = Array.from(counts.entries())
    .map(([phrase, count]) => ({
      phrase,
      count,
      alternative: ALTERNATIVES[phrase],
    }))
    .sort((a, b) => b.count - a.count);

  return {
    wordCount: trimmed.split(RE_SPLIT_WORDS).filter(Boolean).length,
    totalMatches,
    findings,
    highlighted: buildHighlighted(text),
  };
}

/* ------------------------------------------------------------------ */
/*  Stat card                                                         */
/* ------------------------------------------------------------------ */

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Page Component                                                    */
/* ------------------------------------------------------------------ */

/** English strings. A translated content file overrides these through `ui`. */
export const CLICHE_FINDER_STRINGS = {
  textToCheckForCliches: "Text to check for clichés",
  pasteOrTypeYourText: "Paste or type your text here to find clichés...",
  totalWords: "Total Words",
  clichesFound: "Clichés Found",
  uniqueCliches: "Unique Clichés",
  yourTextWithClichesHighlighted: "Your Text with Clichés Highlighted",
  occurrence: "one: occurrence|other: occurrences",
  tryInstead: "Try instead:",
  noClichesDetected: "No clichés detected!",
  noneOfTheCommonCliches:
    "None of the common clichés on our list appear in your text. Nice and fresh.",
} as const;

export default function ClicheFinderWidget() {
  const [text, setText] = useState("");

  const analysis = useMemo(() => analyzeCliches(text), [text]);

  const locale = useToolLocale();
  const strings = useToolUi(CLICHE_FINDER_STRINGS);
  const fmt = useToolNumber();

  return (
    <>
      {/* Input */}
      <textarea
        aria-label={strings.textToCheckForCliches}
        className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        onChange={(e) => setText(e.target.value)}
        placeholder={strings.pasteOrTypeYourText}
        value={text}
      />
      {/* Stats */}
      {analysis && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard
            label={strings.totalWords}
            value={fmt(analysis.wordCount)}
          />
          <StatCard
            label={strings.clichesFound}
            value={fmt(analysis.totalMatches)}
          />
          <StatCard
            label={strings.uniqueCliches}
            value={fmt(analysis.findings.length)}
          />
        </div>
      )}
      {/* Highlighted text */}
      {analysis && analysis.totalMatches > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.yourTextWithClichesHighlighted}
          </h2>
          <div className="whitespace-pre-wrap rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed">
            {analysis.highlighted}
          </div>
        </section>
      )}
      {/* Findings */}
      {analysis && analysis.findings.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.clichesFound}
          </h2>
          <div className="space-y-3">
            {analysis.findings.map((finding) => (
              <div
                className="rounded-lg border border-border bg-card p-4"
                key={finding.phrase}
              >
                <p className="text-foreground text-sm leading-relaxed">
                  <span className="font-medium">{finding.phrase}</span>
                  <span className="text-muted-foreground">
                    {" "}
                    — {fmt(finding.count)}
                    {tPlural(strings.occurrence, locale, finding.count)}
                  </span>
                </p>
                {finding.alternative && (
                  <p className="mt-2 text-muted-foreground text-xs">
                    {strings.tryInstead}{" "}
                    <span className="font-medium text-green-700 dark:text-green-400">
                      {finding.alternative}
                    </span>
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
      {/* No clichés found */}
      {analysis && analysis.totalMatches === 0 && (
        <div className="rounded-lg border border-border bg-card p-5 text-center">
          <p className="font-medium text-foreground">
            {strings.noClichesDetected}
          </p>
          <p className="mt-1 text-muted-foreground text-sm">
            {strings.noneOfTheCommonCliches}
          </p>
        </div>
      )}
    </>
  );
}
